import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Square, Send, Loader2, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { VOICE } from "@/constants/testIds";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useTTSPlayer from "@/hooks/useTTSPlayer";

export default function VoiceChat({ conversationId, scenario, onMessage, autoStarter, onStarterSent }) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiReply, setAiReply] = useState("");
  const [textInput, setTextInput] = useState("");
  const [autoPlay, setAutoPlay] = useState(true);

  const { audioRef, play: playBase64Audio } = useTTSPlayer();
  const starterFiredRef = useRef(false);

  const sendAudio = async (blob) => {
    setProcessing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      const { data } = await api.post(
        `/voice/turn?conversation_id=${conversationId}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setTranscript(data.transcript);
      setAiReply(data.reply);
      onMessage && onMessage({ user: data.transcript, assistant: data.reply });
      if (autoPlay && data.audio_base64) playBase64Audio(data.audio_base64);
    } catch (e) {
      if (e?.response?.status === 402 && e.response.data?.detail === "time_expired") {
        toast.error("Tu llamada de 20 minutos terminó. Recarga para continuar.");
        navigate("/precios?reason=time_expired&plan=extra_time");
      } else {
        toast.error(e.response?.data?.detail || "Error al procesar audio");
      }
    } finally {
      setProcessing(false);
    }
  };

  const recorder = useAudioRecorder({ onChunk: sendAudio });

  useEffect(() => {
    return () => recorder.cleanup();
  }, []);

  // initial idle waveform
  useEffect(() => {
    recorder.drawIdle();
  }, []);

  const handleStart = async () => {
    setTranscript("");
    setAiReply("");
    await recorder.start();
  };

  const sendText = async () => {
    if (!textInput.trim()) return;
    setProcessing(true);
    const userMsg = textInput.trim();
    setTextInput("");
    setTranscript(userMsg);
    setAiReply("");
    try {
      const { data } = await api.post("/chat/message", {
        conversation_id: conversationId,
        message: userMsg,
      });
      setAiReply(data.reply);
      onMessage && onMessage({ user: userMsg, assistant: data.reply });
      if (autoPlay) {
        const tts = await api.post("/voice/tts", { text: data.reply });
        playBase64Audio(tts.data.audio_base64);
      }
    } catch (e) {
      if (e?.response?.status === 402 && e.response.data?.detail === "time_expired") {
        toast.error("Tu llamada de 20 minutos terminó. Recarga para continuar.");
        navigate("/precios?reason=time_expired&plan=extra_time");
      } else {
        toast.error(e.response?.data?.detail || "Error al enviar mensaje");
      }
    } finally {
      setProcessing(false);
    }
  };

  const replayReply = async () => {
    if (!aiReply) return;
    try {
      const { data } = await api.post("/voice/tts", { text: aiReply });
      playBase64Audio(data.audio_base64);
    } catch (e) {
      toast.error("No se pudo reproducir el audio");
    }
  };

  // Auto-send starter message — fires exactly once per conversation
  useEffect(() => {
    if (!autoStarter || !conversationId || starterFiredRef.current) return;
    starterFiredRef.current = true;

    const run = async () => {
      setTranscript(autoStarter);
      setAiReply("");
      setProcessing(true);
      try {
        const { data } = await api.post("/chat/message", {
          conversation_id: conversationId,
          message: autoStarter,
        });
        setAiReply(data.reply);
        setProcessing(false);
        onMessage && onMessage({ user: autoStarter, assistant: data.reply });
        onStarterSent && onStarterSent();
        if (autoPlay) {
          try {
            const tts = await api.post("/voice/tts", { text: data.reply });
            playBase64Audio(tts.data.audio_base64);
          } catch (err) {
            if (process.env.NODE_ENV !== "production") {
              if (process.env.NODE_ENV === "development") {
                console.warn("TTS auto-play failed (non-critical):", err);
              }
            }
          }
        }
      } catch (e) {
        setProcessing(false);
        toast.error("No se pudo iniciar el manual");
      }
    };
    run();
  }, [autoStarter, conversationId, autoPlay, onMessage, onStarterSent]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div
        data-testid={VOICE.waveform}
        className="w-full h-32 bg-zinc-900 rounded-2xl flex items-center justify-center overflow-hidden border-4 border-zinc-800 relative"
      >
        <canvas ref={recorder.canvasRef} width={400} height={120} className="w-full h-full" />
        {processing && (
          <div className="absolute inset-0 bg-zinc-900/80 flex items-center justify-center gap-2 text-white">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-semibold">Procesando…</span>
          </div>
        )}
      </div>

      <audio ref={audioRef} className="hidden" />

      <div className="flex gap-3">
        {!recorder.recording ? (
          <Button
            data-testid={VOICE.recordBtn}
            onClick={handleStart}
            disabled={processing}
            className="flex-1 h-16 text-lg font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center justify-center gap-3 shadow-lg"
          >
            <Mic className="w-6 h-6" />
            Hablar
          </Button>
        ) : (
          <Button
            data-testid={VOICE.stopBtn}
            onClick={recorder.stop}
            className="flex-1 h-16 text-lg font-bold bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl flex items-center justify-center gap-3 animate-pulse"
          >
            <Square className="w-6 h-6" fill="currentColor" />
            Detener y enviar
          </Button>
        )}
        {aiReply && (
          <Button
            data-testid={VOICE.playReplyBtn}
            onClick={replayReply}
            variant="outline"
            className="h-16 px-4 rounded-xl border-2 border-zinc-300"
          >
            <Volume2 className="w-6 h-6" />
          </Button>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          data-testid={VOICE.textInput}
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendText()}
          placeholder="O escribe tu pregunta…"
          disabled={processing}
          className="h-14 text-base rounded-xl border-2 border-zinc-300 focus:border-blue-800"
        />
        <Button
          data-testid={VOICE.sendTextBtn}
          onClick={sendText}
          disabled={processing || !textInput.trim()}
          className="h-14 px-4 bg-blue-800 hover:bg-blue-900 text-white rounded-xl"
        >
          <Send className="w-5 h-5" />
        </Button>
      </div>

      {transcript && (
        <div data-testid={VOICE.transcript} className="bg-zinc-50 border border-zinc-200 p-4 rounded-xl">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Tú dijiste</div>
          <p className="text-base text-zinc-800 leading-relaxed">{transcript}</p>
        </div>
      )}

      {aiReply && (
        <div data-testid={VOICE.reply} className="bg-blue-50 border-l-4 border-l-blue-800 p-4 rounded-r-xl">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-900 mb-1">Asistente</div>
          <p className="text-lg text-zinc-900 leading-relaxed font-medium whitespace-pre-wrap">{aiReply}</p>
        </div>
      )}
    </div>
  );
}
